import { CommonModule } from '@angular/common';
import { AbstractControl, ValidationErrors } from '@angular/forms';
import { Component, Input, inject } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';

type ShowMode = 'touched' | 'dirty' | 'always';

@Component({
  selector: 'lib-form-field-error',
  standalone: true,
  imports: [CommonModule],
  template: `
    @if (message; as text) {
      <small class="text-red-500 text-xs block mt-1">{{ text }}</small>
    }
  `,
})
export class FormFieldErrorComponent {
  private readonly translate = inject(TranslocoService);

  @Input() control: AbstractControl | null = null;
  @Input() label = '';
  @Input() showMode: ShowMode = 'touched';
  @Input() messages?: Record<string, string>;

  private readonly fallbackMessages: Record<
    string,
    (error: ValidationErrors[keyof ValidationErrors], label: string) => string
  > = {
    required: (_, field) =>
      this.translateFallback('required', `${field} is required.`, {
        label: field,
      }),
    email: (_, field) =>
      this.translateFallback(
        'email',
        `${field} must be a valid email address.`,
        { label: field },
      ),
    minlength: (error: any, field) =>
      this.translateFallback(
        'minlength',
        `${field} must be at least ${error?.requiredLength ?? 0} characters.`,
        {
          label: field,
          requiredLength: error?.requiredLength ?? 0,
        },
      ),
    maxlength: (error: any, field) =>
      this.translateFallback(
        'maxlength',
        `${field} must be at most ${error?.requiredLength ?? 0} characters.`,
        {
          label: field,
          requiredLength: error?.requiredLength ?? 0,
        },
      ),
    min: (error: any, field) =>
      this.translateFallback(
        'min',
        `${field} must be greater than or equal to ${error?.min ?? ''}.`,
        { label: field, min: error?.min ?? '' },
      ),
    max: (error: any, field) =>
      this.translateFallback(
        'max',
        `${field} must be less than or equal to ${error?.max ?? ''}.`,
        { label: field, max: error?.max ?? '' },
      ),
    pattern: (_, field) =>
      this.translateFallback('pattern', `${field} has an invalid format.`, {
        label: field,
      }),
    passwordMismatch: (_, field) =>
      this.translateFallback(
        'passwordMismatch',
        `${field} values do not match.`,
        { label: field },
      ),
  };

  get message(): string | null {
    const control = this.control;
    if (!control || !control.errors) {
      return null;
    }

    if (!this.shouldShow(control)) {
      return null;
    }

    const [firstKey] = Object.keys(control.errors);
    if (!firstKey) {
      return null;
    }

    return this.resolveMessage(firstKey, control.errors[firstKey]);
  }

  private shouldShow(control: AbstractControl): boolean {
    const mode = this.showMode ?? 'touched';
    if (mode === 'always') {
      return control.invalid;
    }

    if (mode === 'dirty') {
      return control.invalid && control.dirty;
    }

    return control.invalid && (control.touched || control.dirty);
  }

  private resolveMessage(
    key: string,
    errorValue: ValidationErrors[keyof ValidationErrors],
  ): string | null {
    const label = this.resolveLabel();
    const params = {
      label,
      ...((typeof errorValue === 'object' && errorValue !== null
        ? errorValue
        : {}) as Record<string, unknown>),
    };

    const customMessage = this.messages?.[key];
    if (customMessage) {
      return (
        this.tryTranslate(customMessage, params) ??
        customMessage.replace('{{label}}', label)
      );
    }

    const translated =
      this.tryTranslate(`form.validation.${key}`, params) ??
      this.tryTranslate(`validation.${key}`, params);
    if (translated) {
      return translated;
    }

    const fallback = this.fallbackMessages[key];
    if (fallback) {
      return fallback(errorValue, label);
    }

    return (
      this.tryTranslate('form.validation.default', {
        ...params,
        errorKey: key,
      }) ?? `${label} is invalid.`
    );
  }

  private resolveLabel(): string {
    if (!this.label) {
      return (
        this.tryTranslate('form.validation.field') ??
        this.tryTranslate('validation.field') ??
        'Field'
      );
    }

    return this.tryTranslate(this.label) ?? this.label;
  }

  private translateFallback(
    key: string,
    fallback: string,
    params: Record<string, unknown>,
  ): string {
    return (
      this.tryTranslate(`form.validation.fallback.${key}`, params) ??
      this.tryTranslate(`validation.fallback.${key}`, params) ??
      fallback
    );
  }

  private tryTranslate(
    key: string,
    params?: Record<string, unknown>,
  ): string | null {
    const translated = this.translate.translate(key, params);
    return translated && translated !== key ? translated : null;
  }
}
